import React, { lazy, Suspense, useEffect, useMemo, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import Heading from 'components/shared/heading';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import { Link } from 'gatsby';
import styles from './styles.module.scss';
import Button from '../../shared/button';
import useVideo from '../../../hooks/use-video';
import { useMedia } from 'react-use';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const MediaWithText = ({
  video = null,
  image = null,
  title = null,
  text = null,
  list = null,
  theme = null,
  reverse = false,
  buttonLabel = null,
  buttonLink = null,
  secondarryButtonLink = null,
  secondarryButtonLabel = null,
  imageFit = 'cover',
  mediaWidth = null,
  alignContent = 'top',
  wistiaVideoId = null,
  titleTag = 'h2',
  blockId = null,
}) => {
  const { videoWrapperRef, videoRef, isVideoMuted, handleVideoEnd } = useVideo();
  const player = useRef(null);
  // Create a ref for the image container
  const mediaContainerRef = useRef(null);
  const [isWistiaVisible, setIsWistiaVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const appendImageToCard = useCallback((cardId) => {
    const imageDiv = document.getElementById(`img-${cardId}`);
    if (imageDiv && mediaContainerRef.current) {
      mediaContainerRef.current.appendChild(imageDiv);
    }
  }, []);

  // Handle image appending for this specific card
  useEffect(() => {
    if (image?.id && !video && !wistiaVideoId) {
      appendImageToCard(image.id);
    }
  }, [image, video, appendImageToCard, wistiaVideoId]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const isMobile = useMedia('(max-width: 767px)', false);

  const [containerRef, isInView] = useInView({ threshold: 0.7 });

  useEffect(() => {
    if (wistiaVideoId && player.current) {
      if (isInView) {
        player.current?.play?.();
      } else {
        player.current?.pause?.();
      }
    }
  }, [wistiaVideoId, player, isInView]);

  const placeStyleToWistia = useCallback((wistiaElementParam) => {
    const wistiaElement = wistiaElementParam; // || wistiaPlayerRef.current;
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
          /* This CSS is now safely inside the Shadow DOM */
          .w-vulcan--background {
                display: none !important;
           }
        `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (event) => {
      const wistiaPlayer = event?.target;
      if (wistiaPlayer) {
        placeStyleToWistia(wistiaPlayer);
        setTimeout(() => {
          setIsWistiaVisible(true);
        }, 300);
        if (isInView) {
          wistiaPlayer.play();
        } else {
          wistiaPlayer.pause();
        }
      }
    },
    [isInView, placeStyleToWistia]
  );

  const hasMedia = useMemo(
    () => !!(image || video || wistiaVideoId),
    [image, video, wistiaVideoId]
  );
  const hasText = useMemo(() => !!(text || list), [text, list]);

  const isPhone = useMemo(() => buttonLink?.includes('tel:'), [buttonLink]);
  const isSecondarryButtonPhone = useMemo(
    () => secondarryButtonLink?.includes('tel:'),
    [secondarryButtonLink]
  );

  if (!hasMedia || !hasText) {
    return null;
  }
  return (
    <section
      id={blockId}
      className={cn(styles.mediaWrapper, {
        [styles.reverse]: reverse,
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
      ref={containerRef}
    >
      <div className={styles.inner}>
        {title && isClient && isMobile && (
          <Heading className={styles.title} tag={titleTag} size="xl">
            {title}
          </Heading>
        )}
        <div className="container" ref={videoWrapperRef}>
          <div className={styles.mediaContainer}>
            <div
              className={cn(styles.mediaBox, styles[alignContent], {
                [styles.fadeIn]: isInView,
                [styles.contain]: imageFit === 'contain',
              })}
            >
              <div style={mediaWidth ? { maxWidth: `${mediaWidth}%` } : {}} ref={mediaContainerRef}>
                {video && !wistiaVideoId && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    muted={isVideoMuted}
                    controls={!isVideoMuted}
                    playsInline
                    onEnded={handleVideoEnd}
                    ref={videoRef}
                  >
                    <source src={video.url} type="video/mp4" />
                  </video>
                )}
                {isClient && wistiaVideoId && (
                  <Suspense fallback={null}>
                    <WistiaPlayerLazy
                      mediaId={wistiaVideoId}
                      bigPlayButton={false}
                      muted={isVideoMuted}
                      className={cn(styles.wistiaPlayer, { [styles.visible]: isWistiaVisible })}
                      onApiReady={handleApiReady}
                      poster={false}
                      swatch={false}
                      ref={player}
                      resumable
                    />
                  </Suspense>
                )}
              </div>
            </div>
            <div className={cn(styles.infoBox, styles[alignContent])}>
              {title && (!isClient || !isMobile) && (
                <Heading className={styles.title} tag={titleTag} size="xl">
                  {title}
                </Heading>
              )}
              <div className={styles.text}>
                {text && <div dangerouslySetInnerHTML={{ __html: text }} />}
                {list?.length > 0 && (
                  <ul className={styles.list}>
                    {list.map(({ itemLabel }, index) => (
                      <li key={`list-item-${index}`}>{itemLabel}</li>
                    ))}
                  </ul>
                )}
              </div>

              {buttonLabel && buttonLink && (
                <div className={styles.btnBox}>
                  {/* {buttonHint && <p className={styles.btnHint}>{buttonHint}</p>} */}
                  <Button
                    theme="accent-primary"
                    to={buttonLink}
                    className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
                  >
                    {isPhone && <PhoneIcon />}
                    {buttonLabel}
                    {!isPhone && <ArrowRightIcon />}
                  </Button>
                </div>
              )}
              {secondarryButtonLink && secondarryButtonLabel && (
                <div className={styles.secondaryBtnBox}>
                  <Link
                    to={secondarryButtonLink}
                    className={styles.secondaryBtn}
                    suppressHydrationWarning
                  >
                    {isSecondarryButtonPhone && <PhoneIcon />}
                    <span suppressHydrationWarning>{secondarryButtonLabel}</span>
                    {!isSecondarryButtonPhone && <ArrowRightIcon />}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

MediaWithText.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
  }),
  image: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
  }),
  imageFit: PropTypes.oneOf(['cover', 'contain']),
  mediaWidth: PropTypes.number,
  title: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  text: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      itemLabel: PropTypes.string,
    })
  ),
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  secondarryButtonLink: PropTypes.string,
  secondarryButtonLabel: PropTypes.string,
  reverse: PropTypes.oneOf([true, false, 1]),
  theme: PropTypes.oneOf(['light', 'dark']),
  alignContent: PropTypes.oneOf(['top', 'center']),
  wistiaVideoId: PropTypes.string,
  blockId: PropTypes.string,
};

export default MediaWithText;
